import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { eur } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Plus, Trash2, Package, Monitor } from "lucide-react";
import { toast } from "sonner";

const ORANGE = "rounded-full bg-[#FF6A00] font-semibold text-white hover:bg-[#E65F00]";
const FIELD = "mt-1 border-zinc-800 bg-zinc-900";
const EMPTY_ITEM = { name: "", description: "", price: 0, unit: "forfait", module: "", stock_link: "" };
const EMPTY_SCREEN = { name: "", email: "", password: "" };

function Empty({ icon: Icon, text }) {
  return <div className="rounded-2xl border border-dashed border-zinc-800 py-12 text-center"><Icon className="mx-auto h-10 w-10 text-zinc-700" /><p className="mt-3 text-zinc-500">{text}</p></div>;
}

function CatalogItemDialog({ inventory, onCreated }) {
  const [open, setOpen] = useState(false);
  const [item, setItem] = useState(EMPTY_ITEM);
  const add = async () => {
    if (!item.name) return toast.error("Nom requis");
    const { data } = await api.post("/catalog", { ...item, price: parseFloat(item.price) || 0, module: item.module || null, stock_link: item.stock_link || null });
    onCreated(data); setItem(EMPTY_ITEM); setOpen(false);
    toast.success("Prestation ajoutée");
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button data-testid="add-catalog-btn" className={ORANGE}><Plus className="mr-1 h-4 w-4" />Prestation</Button></DialogTrigger>
      <DialogContent className="border-zinc-800 bg-zinc-950">
        <DialogHeader><DialogTitle className="text-white">Nouvelle prestation</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label className="text-zinc-400">Nom</Label><Input data-testid="catalog-name" className={FIELD} value={item.name} onChange={(e) => setItem({ ...item, name: e.target.value })} /></div>
          <div><Label className="text-zinc-400">Description</Label><Textarea className={FIELD} value={item.description} onChange={(e) => setItem({ ...item, description: e.target.value })} /></div>
          <div><Label className="text-zinc-400">Prix (€)</Label><Input data-testid="catalog-price" type="number" className={FIELD} value={item.price} onChange={(e) => setItem({ ...item, price: e.target.value })} /></div>
          <div>
            <Label className="text-zinc-400">Réserver un article du stock (optionnel)</Label>
            <Select value={item.stock_link || "none"} onValueChange={(v) => setItem({ ...item, stock_link: v === "none" ? "" : v })}>
              <SelectTrigger data-testid="catalog-stock" className={FIELD}><SelectValue placeholder="Aucun" /></SelectTrigger>
              <SelectContent className="border-zinc-800 bg-zinc-900">
                <SelectItem value="none">Aucun</SelectItem>
                {inventory.map((iv) => <SelectItem key={iv.id} value={iv.id}>{iv.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <p className="mt-1 text-xs text-zinc-500">Le matériel sera réservé automatiquement à l'envoi du devis.</p>
          </div>
          <Button data-testid="save-catalog" onClick={add} className={`w-full ${ORANGE}`}>Ajouter</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function CatalogServicesTab() {
  const [catalog, setCatalog] = useState([]);
  const [inventory, setInventory] = useState([]);
  useEffect(() => {
    api.get("/catalog").then(({ data }) => setCatalog(data));
    api.get("/inventory").then(({ data }) => setInventory(data));
  }, []);
  const del = async (id) => { await api.delete(`/catalog/${id}`); setCatalog(catalog.filter((c) => c.id !== id)); };
  const toggleStore = async (c) => {
    const { data } = await api.put(`/catalog/${c.id}`, { store_visible: c.store_visible === false });
    setCatalog(catalog.map((x) => (x.id === c.id ? data : x)));
  };
  return (
    <>
      <div className="mb-4 flex items-center justify-between">
        <p className="text-sm text-zinc-500">Vos prestations réutilisables dans les devis.</p>
        <CatalogItemDialog inventory={inventory} onCreated={(d) => setCatalog([d, ...catalog])} />
      </div>
      {catalog.length === 0 ? <Empty icon={Package} text="Aucune prestation dans votre catalogue." /> : (
        <div className="overflow-hidden rounded-2xl border border-zinc-800 bg-zinc-900">
          {catalog.map((c) => (
            <div key={c.id} className="flex items-center gap-4 border-b border-zinc-800 p-4 last:border-0">
              <div className="flex-1"><p className="font-medium text-white">{c.name}</p>{c.description && <p className="text-sm text-zinc-500">{c.description}</p>}</div>
              <label className="flex items-center gap-2 text-xs text-zinc-500" title="Afficher cet article dans votre boutique en ligne">
                <Switch data-testid={`store-toggle-${c.id}`} checked={c.store_visible !== false} onCheckedChange={() => toggleStore(c)} />Boutique
              </label>
              <span className="font-display font-semibold text-[#FF6A00]">{eur(c.price)}</span>
              <button onClick={() => del(c.id)} className="p-2 text-zinc-600 hover:text-red-400"><Trash2 className="h-4 w-4" /></button>
            </div>
          ))}
        </div>
      )}
    </>
  );
}

function ScreenAccountDialog({ onCreated }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState(EMPTY_SCREEN);
  const add = async () => {
    if (!f.name || !f.email || !f.password) return toast.error("Tous les champs sont requis");
    try {
      const { data } = await api.post("/screen-accounts", f);
      onCreated(data); setF(EMPTY_SCREEN); setOpen(false);
      toast.success("Compte Écran créé");
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button data-testid="add-screen-btn" className={ORANGE}><Plus className="mr-1 h-4 w-4" />Compte Écran</Button></DialogTrigger>
      <DialogContent className="border-zinc-800 bg-zinc-950">
        <DialogHeader><DialogTitle className="text-white">Nouveau compte Écran</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label className="text-zinc-400">Nom de l'écran</Label><Input data-testid="screen-name" className={FIELD} placeholder="Ex: TV Entrepôt" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} /></div>
          <div><Label className="text-zinc-400">Email de connexion</Label><Input data-testid="screen-email" className={FIELD} value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} /></div>
          <div><Label className="text-zinc-400">Mot de passe</Label><Input data-testid="screen-password" type="text" className={FIELD} value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} /></div>
          <Button data-testid="save-screen" onClick={add} className={`w-full ${ORANGE}`}>Créer le compte</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function ScreenAccountsTab() {
  const [screens, setScreens] = useState([]);
  useEffect(() => { api.get("/screen-accounts").then(({ data }) => setScreens(data)).catch(() => {}); }, []);
  const del = async (id) => { await api.delete(`/screen-accounts/${id}`); setScreens(screens.filter((s) => s.id !== id)); toast.success("Compte supprimé"); };
  return (
    <>
      <div className="mb-4 flex items-center justify-between">
        <div>
          <p className="text-sm text-zinc-300">Comptes « Écran entrepôt »</p>
          <p className="text-xs text-zinc-500">Un accès en lecture seule qui affiche en boucle le chargement du jour + le planning de la semaine sur une TV. Aucune donnée financière.</p>
        </div>
        <ScreenAccountDialog onCreated={(d) => setScreens([...screens, d])} />
      </div>
      {screens.length === 0 ? <Empty icon={Monitor} text="Aucun compte Écran. Créez-en un et connectez-vous dessus sur la TV de l'entrepôt." /> : (
        <div className="overflow-hidden rounded-2xl border border-zinc-800 bg-zinc-900">
          {screens.map((s) => (
            <div key={s.id} className="flex items-center gap-4 border-b border-zinc-800 p-4 last:border-0">
              <Monitor className="h-5 w-5 text-[#FFD500]" />
              <div className="flex-1"><p className="font-medium text-white">{s.name}</p><p className="text-sm text-zinc-500">{s.email}</p></div>
              <button onClick={() => del(s.id)} className="p-2 text-zinc-600 hover:text-red-400"><Trash2 className="h-4 w-4" /></button>
            </div>
          ))}
        </div>
      )}
    </>
  );
}
